'use client';

import { c as _c } from "react/compiler-runtime";
import type { RefObject } from 'react';
import { OptimizedImage } from '@/components/images/optimized-image';
import { AnimatedOutcomePercent } from '@/components/markets/animated-outcome-percent';
import { outcomeLabelTooltipArrowClassName, outcomeLabelTooltipClassName, outcomeLabelTooltipContentStyle, useOutcomeLabelTruncation } from '@/components/markets/outcome-label-tooltip';
import { AppTooltip } from '@/components/ui/app-tooltip';
import { DrainedChip } from '@/components/ui/drained-chip';
import { Icon } from '@/components/ui/icon';
import { Award01IconSolid, Tick02Icon } from '@/components/ui/icon/glyphs';
import { useOutcomeColor } from '@/hooks/chart/use-outcome-color';
import { cn } from '@/lib/ui/utils';
export type OutcomeCardVariant = 'interactive' | 'static' | 'winner';
export interface OutcomeCardProps {
  /** Stable parent entity address used to scope the outcome image identity. */
  entityPubkey: string;
  index: number;
  label: string;
  /** Already-computed percent (0-100). The card never derives it from trading state. */
  percent: number;
  /** Consensus goal percentage shown after the current outcome percentage. */
  goalPercent?: number | undefined;
  /** Hide the percentage entirely — used by the winner when consensus is unparseable. */
  showPercent?: boolean;
  /** Render the percentage with two decimals (live trading view). Resolved views pass `false`. */
  decimals?: boolean;
  imageUrl?: string | null;
  imageNsfw?: boolean;
  /** Accessible name for the image. Defaults to empty (decorative) — the
   *  winner passes a descriptive alt for a11y (FR-021). */
  imageAlt?: string;
  /**
   * Enable the click-to-zoom lightbox preview on the outcome image. Default
   * `false`. For the interactive variant the image becomes its own preview
   * control (its own button) while the rest of the card stays the select
   * target — achieved with the overlay-select layout below, so the two
   * controls never nest as buttons.
   */
  imagePreview?: boolean;
  ticker?: string | null;
  variant: OutcomeCardVariant;
  /** Interactive only — current selection state (drives `aria-pressed`). */
  isSelected?: boolean;
  /** Interactive only — selecting the outcome. When omitted the card is inert. */
  onSelect?: ((index: number) => void) | undefined;
  /** Interactive only — accessible name for the selection control. */
  ariaLabel?: string;
  /** Live view only — category emoji shown when the outcome has no image. */
  fallbackEmoji?: string;
  /** Static (loser) only — render the `Drained` chip for the active wallet's dead position. */
  drained?: boolean;
  /** Already-translated `Drained` label. */
  drainedLabel?: string;
  /** Winner only — already-translated "Winner" label. */
  winnerLabel?: string;
  /** Winner only — already-translated caption under the percent (e.g. "Final consensus"). */
  caption?: string;
  /** Renderer-owned display leaf; bypasses AnimatedOutcomePercent. */
  percentContent?: React.ReactNode;
  /** Renderer-owned goal leaf. */
  goalContent?: React.ReactNode;
  selectionControlRef?: RefObject<HTMLButtonElement | null>;
}

/**
 * Shared outcome card — the single visual for one market outcome, used by the
 * live Outcomes panel (interactive) and the Resolved panel (static losers +
 * elevated winner). The percentage is always an input prop so the same card
 * renders live implied odds or a settled final share without any internal data
 * dependency. See spec 109.
 */
export function OutcomeCard(props) {
  const $ = _c(143);
  const {
    entityPubkey,
    index,
    label,
    percent,
    goalPercent,
    showPercent: t0,
    decimals: t1,
    imageUrl: t2,
    imageNsfw: t3,
    imageAlt,
    imagePreview: t4,
    ticker: t5,
    variant,
    isSelected: t6,
    onSelect,
    ariaLabel,
    fallbackEmoji,
    drained: t7,
    drainedLabel,
    winnerLabel,
    caption,
    percentContent,
    goalContent,
    selectionControlRef
  } = props;
  const showPercent = t0 === undefined ? true : t0;
  const decimals = t1 === undefined ? true : t1;
  const imageUrl = t2 === undefined ? null : t2;
  const imageNsfw = t3 === undefined ? false : t3;
  const imagePreview = t4 === undefined ? false : t4;
  const ticker = t5 === undefined ? null : t5;
  const isSelected = t6 === undefined ? false : t6;
  const drained = t7 === undefined ? false : t7;
  let t8;
  if ($[0] !== index || $[1] !== label) {
    t8 = {
      title: label,
      index
    };
    $[0] = index;
    $[1] = label;
    $[2] = t8;
  } else {
    t8 = $[2];
  }
  const colors = useOutcomeColor(t8);
  const {
    setLabelSpanEl,
    isTruncated
  } = useOutcomeLabelTruncation(label);
  let t9;
  if ($[3] !== goalPercent) {
    t9 = goalPercent !== undefined && Number.isFinite(goalPercent) && goalPercent > 0 ? `/${String(Math.round(goalPercent))}%` : null;
    $[3] = goalPercent;
    $[4] = t9;
  } else {
    t9 = $[4];
  }
  const goalLabel = t9;
  const hasImage = imageUrl !== null && imageUrl !== "";
  const showEmoji = !hasImage && fallbackEmoji !== undefined;
  let t10;
  if ($[5] !== colors.cta || $[6] !== colors.hue || $[7] !== entityPubkey || $[8] !== fallbackEmoji || $[9] !== imageAlt || $[10] !== imageNsfw || $[11] !== imagePreview || $[12] !== imageUrl || $[13] !== index || $[14] !== showEmoji) {
    t10 = (sizeClass, rounded) => showEmoji ? <span className={cn("relative flex shrink-0 items-center justify-center overflow-hidden", sizeClass, rounded, colors.cta)}><span aria-hidden={true} className="flex size-full items-center justify-center text-xl leading-none">{fallbackEmoji}</span></span> : <span className={cn("relative flex shrink-0 items-center justify-center overflow-hidden", sizeClass, rounded, colors.cta, imagePreview && "pointer-events-auto")}><OptimizedImage variant="thumbnail" entity={{
        kind: "outcome",
        marketPubkey: entityPubkey,
        outcomeIndex: index,
        hue: colors.hue,
        imageUrl: imageUrl ?? null,
        nsfw: imageNsfw
      }} alt={imageAlt ?? ""} size={sizeClass} rounded={rounded} preview={imagePreview} loadReveal="none" className="size-full object-cover" /></span>;
    $[5] = colors.cta;
    $[6] = colors.hue;
    $[7] = entityPubkey;
    $[8] = fallbackEmoji;
    $[9] = imageAlt;
    $[10] = imageNsfw;
    $[11] = imagePreview;
    $[12] = imageUrl;
    $[13] = index;
    $[14] = showEmoji;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  const mediaBlock = t10;
  let t11;
  if ($[16] !== colors.text || $[17] !== ticker) {
    t11 = ticker !== null ? <span className={cn("shrink-0 rounded-item bg-background-tertiary px-1.5 py-0.5 font-mono text-caption font-semibold tracking-wide", colors.text)}>{ticker}</span> : null;
    $[16] = colors.text;
    $[17] = ticker;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const tickerChip = t11;
  if (variant === "winner") {
    let t12;
    if ($[19] !== colors.outline) {
      t12 = cn("flex w-full flex-col gap-3 rounded-panel-row border border-transparent p-3", colors.outline);
      $[19] = colors.outline;
      $[20] = t12;
    } else {
      t12 = $[20];
    }
    let t13;
    if ($[21] !== colors.text) {
      t13 = cn("size-4", colors.text);
      $[21] = colors.text;
      $[22] = t13;
    } else {
      t13 = $[22];
    }
    let t14;
    if ($[23] !== t13) {
      t14 = <Icon icon={Award01IconSolid} aria-hidden={true} className={t13} />;
      $[23] = t13;
      $[24] = t14;
    } else {
      t14 = $[24];
    }
    let t15;
    if ($[25] !== colors.text || $[26] !== winnerLabel) {
      t15 = winnerLabel !== undefined && <span className={cn("text-caption font-semibold uppercase tracking-wide", colors.text)}>{winnerLabel}</span>;
      $[25] = colors.text;
      $[26] = winnerLabel;
      $[27] = t15;
    } else {
      t15 = $[27];
    }
    let t16;
    if ($[28] !== t14 || $[29] !== t15) {
      t16 = <div className="flex items-center gap-1.5">{t14}{t15}</div>;
      $[28] = t14;
      $[29] = t15;
      $[30] = t16;
    } else {
      t16 = $[30];
    }
    let t17;
    if ($[31] !== mediaBlock) {
      t17 = mediaBlock("size-12", "rounded-panel-inset");
      $[31] = mediaBlock;
      $[32] = t17;
    } else {
      t17 = $[32];
    }
    let t18;
    if ($[33] !== colors.text) {
      t18 = cn("truncate text-body-lg font-semibold", colors.text);
      $[33] = colors.text;
      $[34] = t18;
    } else {
      t18 = $[34];
    }
    let t19;
    if ($[35] !== label || $[36] !== setLabelSpanEl || $[37] !== t18) {
      t19 = <span ref={setLabelSpanEl} className={t18}>{label}</span>;
      $[35] = label;
      $[36] = setLabelSpanEl;
      $[37] = t18;
      $[38] = t19;
    } else {
      t19 = $[38];
    }
    let t20;
    if ($[39] !== t19 || $[40] !== tickerChip) {
      t20 = <div className="flex min-w-0 flex-1 flex-col gap-1">{t19}{tickerChip}</div>;
      $[39] = t19;
      $[40] = tickerChip;
      $[41] = t20;
    } else {
      t20 = $[41];
    }
    let t21;
    if ($[42] !== colors.text || $[43] !== decimals || $[44] !== percent || $[45] !== showPercent) {
      t21 = showPercent && <AnimatedOutcomePercent value={percent} decimals={decimals} animateOnMount={true} className={cn("text-heading-md font-bold tabular-nums", colors.text)} />;
      $[42] = colors.text;
      $[43] = decimals;
      $[44] = percent;
      $[45] = showPercent;
      $[46] = t21;
    } else {
      t21 = $[46];
    }
    let t22;
    if ($[47] !== caption || $[48] !== colors.text) {
      t22 = caption !== undefined && <span className={cn("text-micro uppercase tracking-wide", colors.text)}>{caption}</span>;
      $[47] = caption;
      $[48] = colors.text;
      $[49] = t22;
    } else {
      t22 = $[49];
    }
    let t23;
    if ($[50] !== t21 || $[51] !== t22) {
      t23 = <div className="flex shrink-0 flex-col items-end">{t21}{t22}</div>;
      $[50] = t21;
      $[51] = t22;
      $[52] = t23;
    } else {
      t23 = $[52];
    }
    let t24;
    if ($[53] !== t17 || $[54] !== t20 || $[55] !== t23) {
      t24 = <div className="flex items-center gap-3">{t17}{t20}{t23}</div>;
      $[53] = t17;
      $[54] = t20;
      $[55] = t23;
      $[56] = t24;
    } else {
      t24 = $[56];
    }
    let t25;
    if ($[57] !== t12 || $[58] !== t16 || $[59] !== t24) {
      t25 = <div data-testid="outcome-card" data-variant="winner" className={t12}>{t16}{t24}</div>;
      $[57] = t12;
      $[58] = t16;
      $[59] = t24;
      $[60] = t25;
    } else {
      t25 = $[60];
    }
    const winnerInner = t25;
    const t26 = !isTruncated;
    let t27;
    if ($[61] !== colors) {
      t27 = outcomeLabelTooltipClassName(colors);
      $[61] = colors;
      $[62] = t27;
    } else {
      t27 = $[62];
    }
    let t28;
    if ($[63] !== colors) {
      t28 = outcomeLabelTooltipContentStyle(colors);
      $[63] = colors;
      $[64] = t28;
    } else {
      t28 = $[64];
    }
    let t29;
    if ($[65] !== colors) {
      t29 = outcomeLabelTooltipArrowClassName(colors);
      $[65] = colors;
      $[66] = t29;
    } else {
      t29 = $[66];
    }
    let t30;
    if ($[67] !== label || $[68] !== t26 || $[69] !== t27 || $[70] !== t28 || $[71] !== t29 || $[72] !== winnerInner) {
      t30 = <AppTooltip content={label} disabled={t26} side="bottom" align="center" delayDuration={100} contentClassName={t27} contentStyle={t28} arrowClassName={t29}>{winnerInner}</AppTooltip>;
      $[67] = label;
      $[68] = t26;
      $[69] = t27;
      $[70] = t28;
      $[71] = t29;
      $[72] = winnerInner;
      $[73] = t30;
    } else {
      t30 = $[73];
    }
    return t30;
  }
  let t12;
  if ($[74] !== ariaLabel || $[75] !== colors.fill || $[76] !== colors.hue || $[77] !== colors.outline || $[78] !== colors.text || $[79] !== decimals || $[80] !== drained || $[81] !== drainedLabel || $[82] !== goalContent || $[83] !== goalLabel || $[84] !== index || $[85] !== isSelected || $[86] !== label || $[87] !== mediaBlock || $[88] !== onSelect || $[89] !== percent || $[90] !== percentContent || $[91] !== selectionControlRef || $[92] !== setLabelSpanEl || $[93] !== showPercent || $[94] !== tickerChip || $[95] !== variant) {
    const surfaceClass = cn("relative box-border flex min-w-0 w-full flex-row items-center gap-2 rounded-overlay border-solid [border-width:var(--border-width-hairline)] border-transparent p-2 text-left", variant === "interactive" ? colors.fill : colors.outline, variant === "interactive" && isSelected && `border-outcome-${colors.hue}-text`);
    let t13;
    if ($[97] !== mediaBlock) {
      t13 = mediaBlock("size-10", "rounded-item");
      $[97] = mediaBlock;
      $[98] = t13;
    } else {
      t13 = $[98];
    }
    let t14;
    if ($[99] !== colors.text || $[100] !== isSelected || $[101] !== variant) {
      t14 = variant === "interactive" && isSelected && <span aria-hidden={true} data-testid="outcome-card-selected-icon" className="pointer-events-none absolute -right-1 -bottom-1 flex size-4 items-center justify-center"><span data-testid="outcome-card-selected-icon-background" className="absolute inset-0 rounded-full bg-background-primary/50 backdrop-blur-sm" /><Icon icon={Tick02Icon} className={cn("relative size-3", colors.text)} /></span>;
      $[99] = colors.text;
      $[100] = isSelected;
      $[101] = variant;
      $[102] = t14;
    } else {
      t14 = $[102];
    }
    let t15;
    if ($[103] !== t13 || $[104] !== t14) {
      t15 = <span className="relative shrink-0">{t13}{t14}</span>;
      $[103] = t13;
      $[104] = t14;
      $[105] = t15;
    } else {
      t15 = $[105];
    }
    const compactMediaBlock = t15;
    let t16;
    if ($[106] !== colors.text) {
      t16 = cn("min-w-0 truncate text-body font-medium tracking-tight", colors.text);
      $[106] = colors.text;
      $[107] = t16;
    } else {
      t16 = $[107];
    }
    let t17;
    if ($[108] !== label || $[109] !== setLabelSpanEl || $[110] !== t16) {
      t17 = <span ref={setLabelSpanEl} className={t16}>{label}</span>;
      $[108] = label;
      $[109] = setLabelSpanEl;
      $[110] = t16;
      $[111] = t17;
    } else {
      t17 = $[111];
    }
    let t18;
    if ($[112] !== drained || $[113] !== drainedLabel) {
      t18 = drained && drainedLabel !== undefined && <DrainedChip label={drainedLabel} className="shrink-0" />;
      $[112] = drained;
      $[113] = drainedLabel;
      $[114] = t18;
    } else {
      t18 = $[114];
    }
    let t19;
    if ($[115] !== t17 || $[116] !== t18 || $[117] !== tickerChip) {
      t19 = <span className="flex w-full min-w-0 items-center gap-1.5">{t17}{tickerChip}{t18}</span>;
      $[115] = t17;
      $[116] = t18;
      $[117] = tickerChip;
      $[118] = t19;
    } else {
      t19 = $[118];
    }
    let t20;
    if ($[119] !== colors.text || $[120] !== decimals || $[121] !== goalContent || $[122] !== goalLabel || $[123] !== percent || $[124] !== percentContent || $[125] !== showPercent) {
      t20 = showPercent && <span className={cn("flex shrink-0 items-baseline gap-1", colors.text)}>{percentContent ?? <AnimatedOutcomePercent value={percent} decimals={decimals} animateOnMount={true} className={cn("shrink-0 text-body-lg font-semibold tabular-nums tracking-tight", colors.text)} />}{goalContent ?? (goalLabel !== null && <span className={cn("text-caption font-semibold tabular-nums", colors.text)}>{goalLabel}</span>)}</span>;
      $[119] = colors.text;
      $[120] = decimals;
      $[121] = goalContent;
      $[122] = goalLabel;
      $[123] = percent;
      $[124] = percentContent;
      $[125] = showPercent;
      $[126] = t20;
    } else {
      t20 = $[126];
    }
    let t21;
    if ($[127] !== t19 || $[128] !== t20) {
      t21 = <span className="flex min-h-0 min-w-0 flex-1 flex-col items-start justify-center gap-0 overflow-hidden leading-5">{t19}{t20}</span>;
      $[127] = t19;
      $[128] = t20;
      $[129] = t21;
    } else {
      t21 = $[129];
    }
    const textStack = t21;
    t12 = variant === "interactive" && onSelect !== undefined ? <div className={cn(surfaceClass, "cursor-pointer")} data-testid="outcome-card" data-variant="interactive"><button ref={selectionControlRef} type="button" className={cn("absolute inset-0 cursor-pointer rounded-overlay border-solid [border-width:var(--border-width-hairline)] border-transparent outline-none", `focus-visible:border-outcome-${colors.hue}-text`)} onClick={() => {
        onSelect(index);
      }} aria-label={ariaLabel} aria-pressed={isSelected} /><span className="pointer-events-none relative z-10 flex min-w-0 flex-1 flex-row items-center gap-2">{compactMediaBlock}{textStack}</span></div> : <div className={surfaceClass} data-testid="outcome-card" data-variant="static">{compactMediaBlock}{textStack}</div>;
    $[74] = ariaLabel;
    $[75] = colors.fill;
    $[76] = colors.hue;
    $[77] = colors.outline;
    $[78] = colors.text;
    $[79] = decimals;
    $[80] = drained;
    $[81] = drainedLabel;
    $[82] = goalContent;
    $[83] = goalLabel;
    $[84] = index;
    $[85] = isSelected;
    $[86] = label;
    $[87] = mediaBlock;
    $[88] = onSelect;
    $[89] = percent;
    $[90] = percentContent;
    $[91] = selectionControlRef;
    $[92] = setLabelSpanEl;
    $[93] = showPercent;
    $[94] = tickerChip;
    $[95] = variant;
    $[96] = t12;
  } else {
    t12 = $[96];
  }
  const chip = t12;
  const t13 = !isTruncated;
  let t14;
  if ($[130] !== colors) {
    t14 = outcomeLabelTooltipClassName(colors);
    $[130] = colors;
    $[131] = t14;
  } else {
    t14 = $[131];
  }
  let t15;
  if ($[132] !== colors) {
    t15 = outcomeLabelTooltipContentStyle(colors);
    $[132] = colors;
    $[133] = t15;
  } else {
    t15 = $[133];
  }
  let t16;
  if ($[134] !== colors) {
    t16 = outcomeLabelTooltipArrowClassName(colors);
    $[134] = colors;
    $[135] = t16;
  } else {
    t16 = $[135];
  }
  let t17;
  if ($[136] !== chip || $[137] !== label || $[138] !== t13 || $[139] !== t14 || $[140] !== t15 || $[141] !== t16) {
    t17 = <AppTooltip content={label} disabled={t13} side="bottom" align="center" delayDuration={100} contentClassName={t14} contentStyle={t15} arrowClassName={t16}>{chip}</AppTooltip>;
    $[136] = chip;
    $[137] = label;
    $[138] = t13;
    $[139] = t14;
    $[140] = t15;
    $[141] = t16;
    $[142] = t17;
  } else {
    t17 = $[142];
  }
  return t17;
}